import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { CiEdit } from "react-icons/ci";
import { BiImport } from "react-icons/bi";
import { FaSearch } from "react-icons/fa";
import { PiExportBold } from "react-icons/pi";
import TextArea from "antd/es/input/TextArea";
import { InputNumber, message, Select, Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import "./style.scss";
import uris from "../../../config/uris";
import CustomModal from "../../../assets/component/modal";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import getBase64 from "../../../helpers/base64";
import CustomTable from "../../../assets/component/table";
import { viewPlaybookAction } from "../../../store/slices/manage-playbook/playbook";
import { addPlaybookQuestionsAction, addPlaybookQuestionsReset, editPlaybookQuestions, editPlaybookQuestionsReset } from "../../../store/slices/manage-playbook/editPlaybookQuestions";
import { exportQuestions, exportQuestionsReset } from "../../../store/slices/manage-playbook/exportQuestions";
import { TextConcat } from "../../../helpers/text-concat";
import exportExcel from "../../../helpers/excel-export";
import { decryptData } from "../../../helpers/encrypt/crypto-encrypt";

const { Option } = Select;

const tableHeaderData = [
  {
    title: "DP Codes",
    dataIndex: "dpCodes",
    key: "dpCodes",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.dpCodes?.localeCompare(b.dpCodes),
  },
  {
    title: "Criteria",
    dataIndex: "criteria",
    key: "criteria",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.criteria?.localeCompare(b.criteria),
  },
  {
    title: "Inclusion",
    dataIndex: "inclusion",
    key: "inclusion",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.inclusion?.localeCompare(b.inclusion),
  },
  {
    title: "Exclusion",
    dataIndex: "exclusion",
    key: "exclusion",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.exclusion?.localeCompare(b.exclusion),
  },
  {
    title: "Important Note",
    dataIndex: "importantNote",
    key: "importantNote",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.importantNote?.localeCompare(b.importantNote),
  },
  {
    title: "Search Query",
    dataIndex: "searchQuery",
    key: "searchQuery",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.searchQuery?.localeCompare(b.searchQuery),
  },
  {
    title: "Final Unit",
    dataIndex: "finalUnit",
    key: "finalUnit",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.finalUnit?.localeCompare(b.finalUnit),
  },
  {
    title: "Data Type",
    dataIndex: "dataType",
    key: "dataType",
    width: 150,
    render: (text) => (<TextConcat text={text} maxLength={100} />),
    sorter: (a, b) => a.dataType?.localeCompare(b.dataType),
  },
  {
    title: "Actions",
    dataIndex: "actions",
    key: "actions",
    width: 150,
  },
];

const menuData = (playbookDetails) => {
  return {
    title: "View Playbook",
    parentMenu: {
      name: "Manage Playbook",
      link: uris?.managePlaybook,
    },
    subMenu: {
      name: playbookDetails?.playbookName ? playbookDetails?.playbookName : "",
    },
  };
};

export const EditModal = ({
  handleClose,
  selectedObject,
  editPlaybookQuestionsLoading,
  showEditModal,
  playbookId,
  additionalNote,
  additionalNoteContent,
  dispatch,
  additionalFields = true,
  dpCodeList,
  dataTypeList,
  setQuestionInput,
  editModal,
  setEditPromptModal,
  setSelectedQuestion,
  promptEvaluationFlag,
  chunkEvaluation,
  setSliderValue,
  isMandatory = true
}) => {

  const [formData, setFormData] = useState({
    criteria: "",
    inclusion: "",
    exclusion: "",
    importantNote: "",
    searchQuery: "",
    finalUnit: "",
    dataType: "",
    parentDpCode: "",
    excludeRule: "",
    nullReasoning: "",
    vectorWeight: "",
    keywordWeight: ""
  })

  const [formErrors, setFormErrors] = useState({ criteria: "", searchQuery: "", finalUnit: "" })
  const antIcon = (
    <LoadingOutlined style={{ fontSize: 13, color: "white" }} spin />
  );

  const validateFinalUnit = (value) => {
    if (!value) return "";

    const units = value?.split(/\/|\|\|/)?.filter(Boolean);

    if (units?.length < 2) {
      return "* Final Unit should contain at least two values (e.g. Yes/No or Yes||No)";
    }

    const regex = /^[^\/|]+((\/|\|\|)[^\/|]+)*$/;

    if (!regex.test(value)) {
      return "* Final Unit should be in format: Yes/No/NA or Yes||No||NA";
    }

    return "";
  };


  const updateField = (key, value) => {
    setFormData(prev => {
      let updated = { ...prev, [key]: value };

      if (key === "dataType" && (value === "Text" || value === "Number")) {
        updated.finalUnit = "";
      }

      if (key === "vectorWeight") {
        let v = parseFloat(value);

        if (!isNaN(v)) {
          v = parseFloat(v?.toFixed(2));

          updated.vectorWeight = v;
          let kw = parseFloat((1 - v)?.toFixed(2));

          updated.keywordWeight = kw;
        } else {
          updated.vectorWeight = "";
          updated.keywordWeight = "";
        }
      }
      return updated;
    });

    if (key === "criteria" && value !== "") {
      setFormErrors(prevErrors => ({ ...prevErrors, criteria: null }))
    }

    if (key === "finalUnit") {
      const error = validateFinalUnit(value);
      setFormErrors(prevErrors => ({ ...prevErrors, finalUnit: error }));
    }
  }
  const renderField = ({ label, placeholder, value, onChange, rows = 1, additionalContent, isMandatory, isSelect = false, selectOptions = [], disabled = false, isNumber = false, step }) => (
    <div className="edit-playbook-modal">
      <div className="modal-body-title">{label} {isMandatory && <span className="mandatory-icon">*</span>}</div>
      <div className="row-content">
        {isSelect ? (
          <div className="select-wrapper">
            <Select
              placeholder={placeholder}
              showSearch
              value={value || null}
              allowClear
              onChange={(val) => onChange(val ?? "")}
              style={{ width: "100%" }}
              disabled={chunkEvaluation || (promptEvaluationFlag && !editModal) || disabled}
            >
              {selectOptions?.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </div>
        ) : isNumber ? (
          <InputNumber
            style={{ width: "100%" }}
            placeholder={placeholder}
            disabled={(!editModal && (promptEvaluationFlag || chunkEvaluation)) || disabled}
            value={value}
            onChange={(val) => onChange(val ?? "")}
            min={0}
            max={1}
            step={step}
            onKeyPress={(e) => {
              const currentValue = e.target.value;
              const key = e.key;

              if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter']?.includes(key)) {
                return;
              }

              if (key === '.' && !currentValue?.includes('.')) {
                return;
              }

              // Allow: numbers 0-9
              if (key >= '0' && key <= '9') {
                if (currentValue.length >= 4) {
                  e.preventDefault();
                  return;
                }
                return;
              }
              e.preventDefault();
            }}

            type="Number"
            formatter={(v) => {
              if (v === '' || v === null || v === undefined) return '';
              const num = parseFloat(v);
              if (isNaN(num) || String(v)?.toLowerCase()?.includes('e')) return '';
              return num;
            }}
            parser={(v) => {
              if (v === '' || v === null || v === undefined) return '';
              if (String(v)?.toLowerCase()?.includes('e')) return '';
              const num = parseFloat(v);
              return isNaN(num) ? '' : num;
            }}
          />
        ) : (
          <TextArea
            rows={rows}
            placeholder={placeholder}
            disabled={(!editModal && (promptEvaluationFlag || chunkEvaluation)) || disabled}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (e.target.value !== "") {
                setFormErrors((prevErrors) => ({
                  ...prevErrors,
                  criteria: null,
                }))
              }
            }
            }
          />
        )}
        {additionalContent && (
          <div className="additional-content">{additionalContent}</div>
        )}
      </div>
    </div>
  );

  const handleTest = () => {
    let errors = {};
    const finalUnitError = validateFinalUnit(formData.finalUnit);
    if ((formData?.dataType === "Select" || formData?.dataType === "MultiSelect") && finalUnitError) {
      errors.finalUnit = finalUnitError;
      setFormErrors(errors);
      message.error("Final Unit Should be in valid format")
      return;
    }
    if (!formData.criteria) {
      errors.criteria = "* Criteria field can't be empty";
      setFormErrors(errors)
    } else if (chunkEvaluation && !formData.searchQuery) {
      errors.searchQuery = "* Search Query cannot be empty";
      setFormErrors(errors)
    } else {
      let formattedText = `Criteria:\n${formData.criteria
        .replace(/\r\n/g, '\n')}`;

      if (formData.inclusion || formData.exclusion || formData.importantNote) {
        if (formData.inclusion) {
          formattedText += `\nInclude:\n${formData.inclusion
            .replace(/\r\n/g, '\n')}`;
        }
        if (formData.exclusion) {
          formattedText += `\nExclude:\n${formData.exclusion
            .replace(/\r\n/g, '\n')}`;
        }
        if (formData.importantNote) {
          formattedText += `\n${formData.importantNote
            .replace(/\r\n/g, '\n')}`;
        }
      }
      setQuestionInput(promptEvaluationFlag ? formattedText : formData?.searchQuery)
      setSelectedQuestion((prev) => ({
        ...prev,
        selectedRow: {
          ...prev?.selectedRow,
          criteria: formData.criteria,
          inclusion: formData.inclusion,
          exclusion: formData.exclusion,
          importantNote: formData.importantNote,
          dataType: formData.dataType,
          finalUnits: formData.finalUnit,
          searchQuery: formData.searchQuery,
          vts: formData?.vectorWeight || "0.8",
          fts: formData?.keywordWeight || "0.2"
        }
      }));
      setSliderValue({ vectorWeight: formData?.vectorWeight || "0.8", keywordWeight: formData?.keywordWeight || "0.2" })
      setEditPromptModal(false)
    }
  }

  const handleSubmit = (value) => {
    let errors = {};
    const finalUnitError = validateFinalUnit(formData.finalUnit);
    if ((formData?.dataType === "Select" || formData?.dataType === "MultiSelect") && finalUnitError) {
      errors.finalUnit = finalUnitError;
      setFormErrors(errors);
      message.error("Final Unit Should be in valid format")
      return;
    }
    if (!formData.criteria) {
      errors.criteria = "* Criteria field can't be empty";
      setFormErrors(errors)
    } else {
      const payload = {
        isEdit: true,
        playbookId: playbookId,
        isSearchQueryUpdated: (!selectedObject?.searchQuery?.length) && (formData?.searchQuery?.length > 0),
        questionData: {
          questionId: value,
          criteria: formData.criteria,
          inclusion: formData.inclusion,
          exclusion: formData.exclusion,
          importantNote: formData.importantNote,
          searchQuery: formData.searchQuery,
          vts: formData.vectorWeight || "",
          fts: formData.keywordWeight || "",
          dataType: formData.dataType,
          finalUnits: formData.finalUnit,
          parentDpCode: formData.parentDpCode,
          excludeRule: formData.excludeRule,
          nullReasoning: formData.nullReasoning,
        },
      };
      dispatch(editPlaybookQuestions(payload));
    }
  };

  useEffect(() => {
    if (showEditModal) {
      setFormData({
        criteria: selectedObject.criteria || "",
        inclusion: selectedObject.inclusion || "",
        exclusion: selectedObject.exclusion || "",
        importantNote: selectedObject.importantNote || "",
        searchQuery: selectedObject.searchQuery || "",
        vectorWeight: selectedObject.vts || "",
        keywordWeight: selectedObject.fts || "",
        finalUnit: selectedObject.finalUnits || "",
        dataType: selectedObject.dataType || "",
        parentDpCode: selectedObject.parentDpCode || "",
        excludeRule: selectedObject.excludeRule || "",
        nullReasoning: selectedObject.nullReasoning || ""
      })
    }
  }, [showEditModal]);

  return (
    <>
      {renderField({
        label: "Criteria",
        placeholder: "Enter the question",
        value: formData.criteria,
        onChange: (e) => updateField("criteria", e),
        rows: 2,
        additionalContent: formErrors.criteria && (
          <div className="error-message">{formErrors.criteria}</div>
        ),
        isMandatory: true,
        disabled: chunkEvaluation
      })}

      {renderField({
        label: "Inclusion",
        placeholder: "Enter the inclusion",
        value: formData.inclusion,
        onChange: (e) => updateField("inclusion", e),
        rows: 2,
        disabled: chunkEvaluation
      })}

      {renderField({
        label: "Exclusion",
        placeholder: "Enter the exclusion",
        value: formData.exclusion,
        onChange: (e) => updateField("exclusion", e),
        rows: 2,
        disabled: chunkEvaluation
      })}

      {renderField({
        label: "Important Note",
        placeholder: "Enter the important note",
        value: formData.importantNote,
        onChange: (e) => updateField("importantNote", e),
        rows: 3,
        disabled: chunkEvaluation
      })}

      {renderField({
        label: "Search Query",
        placeholder: "Enter the search query",
        value: formData.searchQuery,
        additionalContent: formErrors.searchQuery && (
          <div className="error-message">{formErrors.searchQuery}</div>
        ),
        onChange: (e) => updateField("searchQuery", e),
        rows: 3,
        disabled: !editModal && (selectedObject?.searchQuery && selectedObject?.searchQuery?.length !== 0) || promptEvaluationFlag
      })}

      {renderField({
        label: "Semantic Weight",
        placeholder: "Enter the vector weight",
        value: formData.vectorWeight,
        onChange: (e) => updateField("vectorWeight", e),
        disabled: chunkEvaluation ? false : true,
        isNumber: true,
        step: 0.05
      })}

      {renderField({
        label: "Keyword Weight",
        placeholder: "Enter the keyword weight",
        value: formData.keywordWeight,
        onChange: (e) => updateField("keywordWeight", e),
        disabled: true,
        isNumber: true,
        step: 0.05
      })}

      {renderField({
        label: "Data Type",
        placeholder: "Select data type",
        value: formData.dataType,
        onChange: (val) => updateField("dataType", val),
        isSelect: true,
        selectOptions: dataTypeList,
        disabled: chunkEvaluation
      })}

      {renderField({
        label: "Final Unit",
        placeholder: "Enter the Final Unit",
        value: formData.finalUnit,
        onChange: (e) => {
          const sanitizedValue = e;
          updateField("finalUnit", sanitizedValue);
        },
        disabled: formData?.dataType === "Text" || formData?.dataType === "Number" || chunkEvaluation,
        additionalContent: formErrors.finalUnit && (
          <div className="error-message">{formErrors.finalUnit}</div>
        ),
      })}

      {additionalFields &&
        renderField({
          label: "Parent Code",
          placeholder: "Select the DP Code",
          value: formData.parentDpCode,
          onChange: (val) => updateField("parentDpCode", val),
          isSelect: true,
          selectOptions: dpCodeList?.filter(
            (item) => item !== selectedObject?.dpCode
          ),
        })}

      {additionalFields && renderField({
        label: "Exclude Rule",
        placeholder: "Enter the value",
        value: formData.excludeRule,
        onChange: (e) => updateField("excludeRule", e),
      })}

      {additionalFields && renderField({
        label: "Null Reasoning",
        placeholder: "Enter the value",
        value: formData.nullReasoning,
        onChange: (e) => updateField("nullReasoning", e),
      })}

      {!editModal && additionalNote && (
        <div className="additional-note">{additionalNoteContent}</div>
      )}
      <div className="action-buttons">
        {editModal && <div className="button-wrapper test" onClick={() => handleTest(selectedObject?.questionId)}>
          Test
        </div>}
        {!editModal && <div
          className="button-wrapper submit"
          onClick={() => handleSubmit(selectedObject?.questionId)}
        >
          {editPlaybookQuestionsLoading ? <Spin className="submit-loading" size="small" indicator={antIcon} /> : "Submit"}
        </div>}
        <div className="button-wrapper cancel" onClick={handleClose}>
          Cancel
        </div>
      </div>
    </>
  );
};

const ConfirmationModal = ({ fileName, handleConfirmImport, handleClose }) => {

  return (
    <div className="confirmation-modal-container">
      <div>Are you sure you want to import {" "} <strong>{fileName}</strong>{"?"}</div>
      <div className="action-buttons">
        <div className="button-wrapper submit" onClick={() => handleConfirmImport()}>Yes</div>
        <div className="button-wrapper cancel" onClick={() => handleClose()}>No</div>
      </div>
    </div>
  )
}

const ViewPlaybook = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [playbookQuestions, setPlaybookQuestions] = useState(null);
  const [breadcrumbsData, setBreadcrumbsData] = useState(null);
  const [formattedTableData, setFormattedTableData] = useState({ dataSource: [], columns: [] });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedObject, setSelectedObject] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [importFileName, setImportFileName] = useState("");
  const [base64FileName, setBase64FileName] = useState("");
  const [showImportQuestionModal, setShowImportQuestionModal] = useState(false);
  const [exportQuestionLoading, setExportQuestionLoading] = useState(false);
  const [userRole, setUserRole] = useState("")

  const fileInputRef = useRef(null);

  const dispatch = useDispatch();
  const { id: playbookId } = useParams();
  const { data: viewPlaybookResponse, isLoading: viewPlaybookLoading } = useSelector((state) => state?.viewPlaybook);
  const { isLoading: addPlaybookQuestionsLoading, error: addPlaybookQuestionsError, success: addPlaybookQuestionsSuccess, data: addPlaybookQuestionsData } = useSelector((state) => state?.addPlaybookQuestions);
  const { isLoading: exportQuestionsLoading, error: exportQuestionsError, success: exportQuestionsSuccess, data: exportQuestionsData } = useSelector((state) => state?.exportQuestions);
  const { responseData: viewPlaybookDetails } = viewPlaybookResponse || {};
  const {
    data: editPlaybookQuestionsData,
    isLoading: editPlaybookQuestionsLoading,
    success: editPlaybookQuestionsSuccess,
    error: editPlaybookQuestionsError,
  } = useSelector((state) => state?.editPlaybookQuestions);
  const dpCodeList = viewPlaybookDetails?.questionData?.map((data) => data?.dpCode)
  const dataTypeList = ["Text", "Number", "Select", "MultiSelect"];

  const tableStructuredData = (questionsList) => {
    const formatedData = questionsList?.map((question) => {
      const rowData = {
        key: question?.questionId,
        dpCodes: question?.dpCode ? question?.dpCode : "-",
        criteria: question?.criteria ? question?.criteria : "-",
        inclusion: question?.inclusion ? question?.inclusion : "-",
        exclusion: question?.exclusion ? question?.exclusion : "-",
        importantNote: question?.importantNote
          ? question?.importantNote
          : "-",
        searchQuery: question?.searchQuery ? question?.searchQuery : "-",
        finalUnit: question?.finalUnits ? question?.finalUnits : "-",
        dataType: question?.dataType ? question?.dataType : "-",
        model: question?.model ? question?.model : "-",
      };
      if (["admin", "prompt_admin"].includes(userRole)) {
        rowData.actions = (
          <div className="action-icons-container">
            <div
              className="editIcon"
              onClick={() => handleEdit(question?.questionId)}
            >
              <Tooltip title="Edit">
                <span className="action-editIcon"> <CiEdit /> </span>
              </Tooltip>{" "}
            </div>
          </div>
        );
      }
      return rowData;
    });

    return formatedData;
  };

  const handleExportQuestions = () => {
    let payload = { playbookId: playbookId };
    dispatch(exportQuestions(payload));
  }

  const handleExportPlaybook = () => {
    exportExcel({ data: viewPlaybookDetails?.questionData || [], fileName: viewPlaybookDetails?.playbookName })
  }

  const handleImportFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";

    if (file) {
      const fileTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "text/csv", "application/csv"];
      if (fileTypes?.includes(file?.type)) {
        setImportFileName(file?.name);
        try {
          const base64 = await getBase64(file);
          setBase64FileName(base64?.split(";base64,")[1]);
          setShowImportQuestionModal(true);
        } catch (error) {
          message.error("Error converting file to base64:", error);
        }
      } else {
        message.error("Please select a valid .xlsx or .csv file");
      }
    }
  };

  const handleImportQuestions = () => {
    if (fileInputRef) {
      fileInputRef?.current?.click();
    }
  }

  const handleCloseImportQuestionModal = () => {
    setShowImportQuestionModal(false);
  }

  const handleSearch = (value) => {
    setSearchValue(value);
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      searchBasedFilter();
    }
  }

  const searchBasedFilter = () => {
    const searchTerm = searchValue?.trim()?.toLowerCase();

    const filteredData = playbookQuestions?.filter(({ dpCode, criteria }) => {
      const dpCodeText = dpCode?.toLowerCase() || '';
      const criteriaText = criteria?.replace(/[\r\n]+/g, '')?.toLowerCase() || '';
      return dpCodeText?.includes(searchTerm) || criteriaText?.includes(searchTerm);
    });

    setFormattedTableData({ ...formattedTableData, dataSource: tableStructuredData(filteredData) });
  }

  const handleConfirmImport = () => {
    let payload = {
      "playbookId": playbookId,
      "additionalQuestionData": base64FileName
    }
    dispatch(addPlaybookQuestionsAction(payload));
    handleCloseImportQuestionModal();
  }

  const handleEdit = (value) => {
    const response = viewPlaybookDetails?.questionData?.find(
      (item) => item.questionId === value
    );
    if (response) {
      setSelectedObject(response);
      setShowEditModal(true);
    }
  };

  useEffect(() => {
    if (playbookId) {
      let payload = { playbookId: playbookId };
      dispatch(viewPlaybookAction(payload));
    }
  }, [playbookId]);

  useEffect(() => {
    if (viewPlaybookDetails && viewPlaybookDetails?.questionData) {
      setPlaybookQuestions(viewPlaybookDetails?.questionData);
      setBreadcrumbsData(menuData(viewPlaybookDetails));
    }
  }, [viewPlaybookDetails]);

  useEffect(() => {
    const role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'));
    setUserRole(role_decrypt?.role)
  }, [])

  useEffect(() => {
    if (playbookQuestions) {
      const baseColumns = [...tableHeaderData];
      const filteredColumns = (userRole === "admin" || userRole === "prompt_admin")
        ? baseColumns
        : baseColumns.filter(col => col.dataIndex !== "actions");
      setFormattedTableData({ dataSource: tableStructuredData(playbookQuestions), columns: filteredColumns });
    }
  }, [playbookQuestions, userRole]);

  useEffect(() => {
    if (editPlaybookQuestionsSuccess) {
      message.success(editPlaybookQuestionsData?.message);
      dispatch(editPlaybookQuestionsReset());
      setShowEditModal(false);
      if (playbookId) {
        let payload = { playbookId: playbookId };
        dispatch(viewPlaybookAction(payload));
      }
      dispatch(editPlaybookQuestionsReset());
    }
    if (editPlaybookQuestionsError) {
      message.error(editPlaybookQuestionsError.message);
      dispatch(editPlaybookQuestionsReset());
    }
  }, [editPlaybookQuestionsSuccess, editPlaybookQuestionsError]);

  useEffect(() => {
    if (addPlaybookQuestionsError) {
      message?.error(addPlaybookQuestionsError?.message);
    }
    if (addPlaybookQuestionsSuccess) {
      message?.success(addPlaybookQuestionsData?.message);
      let payload = { playbookId: playbookId };
      dispatch(viewPlaybookAction(payload));
    }
    dispatch(addPlaybookQuestionsReset());
  }, [addPlaybookQuestionsError, addPlaybookQuestionsSuccess])

  useEffect(() => {
    if (exportQuestionsError) {
      message?.error(exportQuestionsError?.message);
    }
    if (exportQuestionsSuccess) {
      message?.success("Questions Exported Successfully");
    }
    dispatch(exportQuestionsReset());
  }, [exportQuestionsError, exportQuestionsSuccess])

  return (
    <div className="view-playbook-main-container">
      <Breadcrumbs breadcrumbDetails={breadcrumbsData} />
      <div className="view-playbook-body-section">
        <div className="view-playbook-filter-section">
          <div className="view-playbook-filter-section-left">
            <div className='search-wrapper'>
              <input type="text" className="search-input" placeholder="Search By Dpcode or Question"
                value={searchValue} onChange={(e) => handleSearch(e?.target?.value)} onKeyDown={(e) => handleKeyPress(e)}
              />
              <FaSearch className={`search-icon ${searchValue ? "active-icon" : ""}`} onClick={() => searchBasedFilter()} />
            </div>
          </div>
          <div className="view-playbook-filter-section-right">
            <div className="button-wrapper" onClick={() => handleImportQuestions()}>
              {addPlaybookQuestionsLoading ? <Spin indicator={<LoadingOutlined spin className="spinner" />} /> : <BiImport />}
              Import Questions
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: "none" }}
                onChange={(e) => handleImportFileChange(e)}
              />
            </div>
            <div className="button-wrapper" onClick={() => handleExportQuestions()}>
              {exportQuestionsLoading ? <Spin indicator={<LoadingOutlined spin className="spinner" />} /> : <PiExportBold />}
              Export Questions
            </div>
            <div className="button-wrapper" onClick={() => handleExportPlaybook()}>
              {exportQuestionLoading ? <Spin indicator={<LoadingOutlined spin className="spinner" />} /> : <PiExportBold />}
              Export Playbook
            </div>
          </div>
        </div>
        <div className="playbook-table-section">
          <CustomTable
            page={page}
            limit={limit}
            setPage={setPage}
            setLimit={setLimit}
            tableData={formattedTableData}
            totalRows={formattedTableData?.dataSource?.length}
            isLoading={viewPlaybookLoading}
          />
        </div>
      </div>
      {showEditModal &&
        <CustomModal
          showCloseIcon
          modalTitle={selectedObject?.dpCode}
          handleClose={() => setShowEditModal(false)}
        >
          <EditModal
            showEditModal={showEditModal}
            handleClose={() => setShowEditModal(false)}
            selectedObject={selectedObject}
            editPlaybookQuestionsLoading={editPlaybookQuestionsLoading}
            playbookId={playbookId}
            dispatch={dispatch}
            dpCodeList={dpCodeList}
            dataTypeList={dataTypeList}
          />
        </CustomModal>
      }
      {showImportQuestionModal && (
        <CustomModal
          showCloseIcon
          handleClose={() => handleCloseImportQuestionModal()}
          modalTitle={"Confirm"}
        >
          <ConfirmationModal fileName={importFileName} handleConfirmImport={handleConfirmImport} handleClose={handleCloseImportQuestionModal} />
        </CustomModal>
      )}
    </div >
  );
};

export default ViewPlaybook;